'use client';

import { usePathname } from 'next/navigation';
import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import GreenLogo from '@/components/icons/GreenLogo';
import {
  antiScreenshotElementProps,
  TOP_NAV_HEIGHT_PX,
} from '@/constants/constants';
import { PLAT_MAP_URL } from '@/constants/urls';
import { useFooterButtonFunctions } from '@/features/selectionsPanel/hooks/useFooterButtonFunctions';
import { ShareTabValues } from '@/features/share/shareModal';
import { useShareModal } from '@/hooks/useShareModal';
import { useAuth } from '@/providers/auth-provider';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { useModalContext } from '@/providers/ModalProvider';
import { RootState } from '@/redux/store';

import { useNavigationContext } from '../provider';
import { useNavUtils } from '../side-nav/hooks/useNavUtils';
import FileTab from './fileTab';
import { NavLinkTabs } from './navLinkTabs';
import ProfileOptions from './profileOptions';
import { UtilButtons } from './utilButtons';

type Options = {};

const TopNav: FC<Options> = () => {
  const pathname = usePathname();
  const { openAuthModal } = useModalContext();
  const { hasActiveSession } = useAuth();

  const { onLogoClick } = useNavUtils();

  const { allFeatures } = useSelector((state: RootState) => state.features);
  const { activeFile, fileManagementUtilities, setActiveFile } =
    useFileManagementContext();

  const { aerialMapIsOpen } = useNavigationContext();

  const { openShareFileBoundariesModal } = fileManagementUtilities;

  const {
    shareButtonOptions: {
      disabled: shareButtonDisabled,
      disabledText: shareButtonDisabledText,
      onDisabledClick: shareButtonDisabledClick,
    },
    compReportButtonOptions: {
      disabled: isCompReportButtonDisabled,
      onClick: onCompReportClick,
      onDisabledClick: onCompReportDisabledClick,
    },
    insightReportButtonOptions: {
      onClick: onInsightReportClick,
      disabled: insightReportButtonDisabled,
    },
  } = useFooterButtonFunctions();

  const showActiveSavedSelection = useMemo(() => {
    return pathname?.includes(PLAT_MAP_URL);
  }, [pathname]);

  const showUtilButtons = useMemo(() => {
    return pathname?.includes(PLAT_MAP_URL);
  }, [pathname]);

  const { openShareModal } = useShareModal();

  const onShareClick = useCallback(() => {
    if (!hasActiveSession) {
      openAuthModal('signup');
      return;
    }
    if (activeFile) {
      openShareFileBoundariesModal?.(activeFile, false);
    } else {
      openShareModal({
        allFeatures,
        closeOnOutsideClick: true,
        defaultTab: ShareTabValues.SHARE,
        savedSelection: activeFile ?? undefined, // Open as saved selection if there is an active file
        setActiveFile,
      });
    }
  }, [
    activeFile,
    allFeatures,
    hasActiveSession,
    openAuthModal,
    openShareFileBoundariesModal,
    openShareModal,
    setActiveFile,
  ]);

  return (
    <div
      {...antiScreenshotElementProps}
      className="visible sticky top-0 z-[202] w-full"
      style={{
        height: `${TOP_NAV_HEIGHT_PX}px`,
      }}
    >
      <div className="absolute left-0 top-0 z-[-5] flex h-full w-full border-b-[1px] border-b-gray-20 bg-white">
        {/* This div renders the background and border of the navbar */}
      </div>
      <div className="ml-auto flex h-full w-full max-w-full justify-start sm:flex-wrap">
        <button
          className="flex w-[45px] place-content-center items-center sm:w-[72px]"
          disabled={aerialMapIsOpen}
          type="button"
          onClick={onLogoClick}
        >
          <GreenLogo className="h-[28px] w-[26px]" />
        </button>
        <div className="hidden sm:block">
          <NavLinkTabs />
        </div>

        <div className="flex-1 flex-grow">
          {showActiveSavedSelection && (
            <div className="flex h-full place-content-center items-center">
              <FileTab />
            </div>
          )}
        </div>

        <div className="flex place-content-center items-center sm:space-x-4">
          {showUtilButtons && (
            <UtilButtons
              insightReportButtonDisabled={insightReportButtonDisabled}
              isCompReportButtonDisabled={isCompReportButtonDisabled}
              shareDisabled={shareButtonDisabled || aerialMapIsOpen}
              shareDisabledTooltipText={shareButtonDisabledText}
              onCompReportClick={onCompReportClick}
              onCompReportDisabledClick={onCompReportDisabledClick}
              onInsightReportClick={onInsightReportClick}
              onShare={onShareClick}
              onShareDisabledClick={shareButtonDisabledClick}
            />
          )}

          <ProfileOptions />
        </div>
      </div>
    </div>
  );
};

export default TopNav;
